import React, { useState } from "react";
import PropTypes from "prop-types";
import {
	Stack,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField
} from "@mui/material";
import { isEmpty } from "lodash";
import useApi from "../hooks/useApi";

/**
 * AcceptedDomainAddModal
 *
 * This component renders a modal dialog that allows the addition of a new accepted domain.
 * It validates that a domain is supplied before submitting it to the server.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.config - The configuration object containing API endpoints.
 * @param {boolean} props.isOpen - Flag to show or hide the modal.
 * @param {function} props.onChangeBulk - Callback function to handle bulk changes.
 * @param {string|number} props.environmentId - The environment identifier.
 * @returns {JSX.Element} The rendered AcceptedDomainAddModal component.
 */
const AcceptedDomainAddModal = (props) => {
	const { config, isOpen, onChangeBulk: parentOnChangeBulk, environmentId } = props;
	const [acceptedDomain, setAcceptedDomain] = useState("");
	const [errors, setErrors] = useState({});
	const api = useApi();

	/**
	 * handleDialogClose - Handles closing of the modal dialog.
	 *
	 * @function
	 * @returns {void}
	 */
	const handleDialogClose = () => {
		setErrors({});
		setAcceptedDomain("");
		parentOnChangeBulk({ acceptedDomainAddModalIsOpen: false });
	};

	/**
	 * handleSubmit - Validates input and submits the accepted domain to the server.
	 *
	 * @async
	 * @function
	 * @returns {Promise<void>} Resolves after handling the submit action.
	 */
	const handleSubmit = async () => {
		const newErrors = {};
		if (!acceptedDomain) newErrors.acceptedDomain = "You must supply a valid http/https URL";

		if (!isEmpty(newErrors)) {
			setErrors(newErrors);
			return;
		}

		try {
			const application = await api.acceptedDomain.add({ environmentId, acceptedDomain });
			setAcceptedDomain("");

			parentOnChangeBulk({
				acceptedDomainAddModalIsOpen: false,
				application: application,
				environments: application.environments
			});
		} catch (error) {
			console.error("Failed to add accepted domain:", error);
		}
	};

	return (
		<Dialog open={isOpen} onClose={handleDialogClose} fullWidth>
			<DialogTitle>Add New Accepted Domain</DialogTitle>
			<DialogContent>
				<Stack direction="column" spacing={2} mt={2}>
					<TextField
						label="Accepted Domain"
						value={acceptedDomain}
						onChange={(e) => setAcceptedDomain(e.target.value)}
						error={!!errors.acceptedDomain}
						helperText={errors.acceptedDomain}
						fullWidth
					/>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleDialogClose}>Cancel</Button>
				<Button onClick={handleSubmit}>Submit</Button>
			</DialogActions>
		</Dialog>
	);
};

AcceptedDomainAddModal.propTypes = {
	config: PropTypes.shape({
		uri: PropTypes.shape({
			base: PropTypes.string.isRequired
		}).isRequired
	}).isRequired,
	isOpen: PropTypes.bool.isRequired,
	onChangeBulk: PropTypes.func.isRequired,
	environmentId: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number
	]).isRequired
};

export default AcceptedDomainAddModal;
